/**
 * Hook para revelar o telefone do owner de um tenant (LGPD).
 * Chama a RPC `wl_reveal_tenant_owner_phone` que registra cada acesso
 * em `security_bypass_attempts` para auditoria.
 *
 * @version 1.1.0
 * RELEASE: 2026-06-24 v1.1.0 - Reveal exclusivo: ao revelar um tenant, esconde
 *                              automaticamente qualquer outro previamente revelado.
 *                              Mitiga exfiltração em massa (LGPD): só 1 telefone
 *                              visível por vez na listagem.
 * RELEASE: 2026-05-28 v1.0.0 - Criação.
 */
import { useState, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

export function useWlRevealTenantPhone() {
  // Cache local: tenantId -> telefone revelado (sessão da página)
  const [revealed, setRevealed] = useState<Record<string, string | null>>({});

  const mutation = useMutation({
    mutationFn: async (tenantId: string) => {
      const { data, error } = await supabase.rpc('wl_reveal_tenant_owner_phone', {
        p_tenant_id: tenantId,
      });
      if (error) throw error;
      return { tenantId, phone: (data as string | null) ?? null };
    },
    onSuccess: ({ tenantId, phone }) => {
      // Reveal exclusivo: substitui o estado por apenas o tenant atual,
      // forçando que qualquer telefone previamente revelado seja escondido.
      setRevealed({ [tenantId]: phone });
    },
    onError: (err: unknown) => {
      logger.error('[wl] reveal owner phone failed', err instanceof Error ? err.message : String(err));
    },
  });

  /** Esconde novamente o telefone revelado. */
  const hide = useCallback((tenantId: string) => {
    setRevealed((prev) => {
      const next = { ...prev };
      delete next[tenantId];
      return next;
    });
  }, []);

  return {
    revealed,
    reveal: (tenantId: string) => mutation.mutate(tenantId),
    hide,
    isRevealing: (tenantId: string) =>
      mutation.isPending && mutation.variables === tenantId,
  };
}
